import {type BskyAgent} from '@atproto/api'

import {WsocFetchError} from '#/lib/wsoc/fetch'
import {
  getReauthToken,
  silentReauth as doSilentReauth,
} from '#/lib/wsoc/quicklogin'
import {type SilentReauthResponse} from '#/lib/wsoc/quicklogin/silentReauth'
import {logger} from '#/logger'

export class PermanentReauthFailureError extends Error {
  constructor() {
    super('SilentReauthPermanentFailure')
  }
}

/** Thrown when all retry attempts have been exhausted (non-400 errors). */
export class ExhaustedReauthAttemptsError extends Error {
  constructor() {
    super('SilentReauthAttemptsExhausted')
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function nextOnlineEvent(): Promise<void> {
  if (
    typeof window === 'undefined' ||
    typeof window.addEventListener !== 'function'
  ) {
    // In non-browser environments (React Native, SSR) there are no network events;
    // return a never-resolving promise so the sleep always wins the race.
    return new Promise(() => {})
  }
  return new Promise(resolve => {
    window.addEventListener('online', () => resolve(), {once: true})
  })
}

/**
 * Fetches reauthJwt after a successful login. Best-effort — returns undefined on any failure.
 */
export async function fetchReauthJwt(
  agent: BskyAgent,
): Promise<string | undefined> {
  try {
    const {reauthJwt} = await getReauthToken(agent)
    return reauthJwt
  } catch {
    logger.warn('silentReauth: failed to fetch reauthJwt after login')
    return undefined
  }
}

const MAX_ATTEMPTS = 10
const MAX_BACKOFF_MS = 30_000

/**
 * Exchanges a reauthJwt for fresh session tokens.
 *
 * - HTTP 400 → throws PermanentReauthFailureError immediately (token invalid/expired).
 * - Other errors (network, 5xx, 401, 429) → retried with exponential backoff up to
 *   MAX_ATTEMPTS times, then throws ExhaustedReauthAttemptsError.
 */
export async function fetchSilentReauthWithRetry(
  agent: BskyAgent,
  reauthJwt: string,
): Promise<SilentReauthResponse> {
  let attempt = 0
  while (attempt < MAX_ATTEMPTS) {
    if (attempt > 0) {
      const backoffMs = Math.min(2_000 * 2 ** (attempt - 1), MAX_BACKOFF_MS)
      await Promise.race([sleep(backoffMs), nextOnlineEvent()])
    }
    attempt += 1
    try {
      return await doSilentReauth(agent, reauthJwt)
    } catch (err) {
      if (err instanceof WsocFetchError && err.status === 400) {
        // The reauthJwt itself is invalid or expired — no point retrying.
        throw new PermanentReauthFailureError()
      }
      // Network error, 5xx, or other 4xx (401, 429) — transient, retry with backoff.
      logger.warn('silentReauth: transient error, retrying', {
        attempt,
        status: err instanceof WsocFetchError ? err.status : 'network',
        message: err instanceof Error ? err.message : String(err),
      })
    }
  }
  throw new ExhaustedReauthAttemptsError()
}
